import type { FeatureFlagRegistryEntry, ProductRegistryEntry } from './types';

/**
 * Feature flag registry — pure display data + mapping keys.
 * Adding a new feature flag = adding one entry here + one branch in settingsApi.
 */
export const FEATURE_FLAG_REGISTRY: FeatureFlagRegistryEntry[] = [
  {
    id: 'laminar',
    title: 'Laminar',
    description: 'Data pipeline orchestration and management.',
  },
  {
    id: 'metriq',
    title: 'Metriq',
    description: 'KPI tracking, metric investigations, and AI-driven insights.',
  },
  {
    id: 'observe',
    title: 'Observability',
    description: 'Unified logs, metrics, and traces ingestion and query.',
  },
];

/**
 * Statuses that mean "done" — the product has stopped moving on its own, so
 * polling it further would never end.
 *
 * Degraded is settled: the upgrade finished and the product is serving, with a
 * best-effort component still down. Nothing further changes without someone
 * acting, so it belongs here rather than in the in-progress list.
 *
 * Lives here rather than beside either consumer because both the polling hook
 * and the version tab test the same vocabulary, and a status added to one copy
 * but not the other is a product that either spins forever or settles early.
 */
export const SETTLED_STATUSES = ['Ready', 'Running', 'Degraded', 'Failed', 'Suspended'];

/**
 * Statuses that mean "still moving". A product in one of these is on its way
 * somewhere and a spinner is the right treatment.
 */
export const TRANSITIONAL_STATUSES = ['Updating', 'Pending', 'Creating', 'Resuming'];

/**
 * The word to show while a product is in flight. The operator distinguishes a
 * first install from an upgrade — `Creating` means nothing has ever served,
 * `Updating` means something is being replaced — and saying "Updating" over
 * both throws that away, which is what put "Updating..." against a product
 * being created for the first time.
 */
export function transitionalLabel(status: string | null | undefined): string {
  switch (status) {
    case 'Creating':
      return 'Creating...';
    case 'Resuming':
      return 'Resuming...';
    case 'Pending':
      return 'Pending...';
    default:
      return 'Updating...';
  }
}

export type BadgeStatus =
  | 'active'
  | 'disabled'
  | 'accepted'
  | 'pending'
  | 'suspended'
  | 'error'
  | 'warning'
  | null;

/**
 * The operator's phase vocabulary, mapped to a badge.
 *
 * One definition, beside SETTLED_STATUSES, because both settings tabs render
 * the same statuses and had their own copies. `Degraded` was added to one and
 * not the other, so a product serving with a broken dependency showed amber on
 * the Version Upgrades tab and the same green as a healthy product on Feature
 * Enablement — the surface a user looks at first. That is the third time a
 * status has been added to one copy and not another.
 *
 * Degraded is warning, never active: the product is serving with something
 * broken, which is neither healthy nor failed.
 *
 * Returns null for a status this vocabulary does not know, rather than guessing.
 * The two tabs disagree about what an unrecognised status should look like —
 * Version Upgrades treats it as nothing deployed, Feature Enablement as an
 * enabled product that has not answered yet — and both are defensible, so the
 * default belongs at the call site.
 */
export function toStatusBadge(status: string | null | undefined): BadgeStatus {
  if (!status) return null;
  if (TRANSITIONAL_STATUSES.includes(status)) return 'warning';
  if (['Ready', 'Running'].includes(status)) return 'active';
  if (status === 'Degraded') return 'warning';
  if (status === 'Failed') return 'error';
  if (['Suspended', 'Suspending'].includes(status)) return 'suspended';
  return null;
}

/**
 * Product registry — pure display data + status classification.
 * Adding a new product = adding one entry here + one branch in settingsApi.
 */
export const PRODUCT_REGISTRY: ProductRegistryEntry[] = [
  {
    id: 'metadata-service',
    title: 'Metadata Service',
    description: 'Core metadata catalog and governance engine.',
    upgradingStatuses: ['Updating', 'Pending', 'Creating'],
  },
  {
    id: 'laminar',
    title: 'Laminar',
    description: 'Data pipeline orchestration and management.',
    upgradingStatuses: ['Updating', 'Pending', 'Creating'],
    featureGated: 'laminar',
  },
  {
    id: 'metriq',
    title: 'Metriq',
    description: 'KPI tracking, metric investigations, and AI-driven insights.',
    upgradingStatuses: ['Updating', 'Pending', 'Creating'],
    featureGated: 'metriq',
  },
  // TEMPORARILY HIDDEN — observe is a feature flag (FEATURE_FLAG_REGISTRY)
  // but not yet a product: its version upgrades are deferred (#1182).
  // Re-enable by uncommenting; the matching `observe` branch in
  // settingsApi handles the versions / features lookup.
  // {
  //   id: 'observe',
  //   title: 'Observe',
  //   description: 'Unified logs, metrics, and traces ingestion and query.',
  //   upgradingStatuses: ['Updating', 'Pending', 'Creating'],
  //   featureGated: 'observe',
  // },
];
